import type {
  AccountId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { useSupportTicketAccountId } from "./use-support-ticket-team-id";

/**
 * Filter the global users collection to the users on a specific account.
 * Backs the assignee picker — connected-team tickets should only ever
 * offer the ticket's team's users, and the backend already rejects
 * cross-team assignments.
 */
export function useUsersForAccount(
  accountId: AccountId | undefined,
): readonly AssignableUser[] {
  const collections = useGlobalSupportTicketCollections();
  const result = useLiveQuery(
    (q) =>
      accountId === undefined
        ? undefined
        : q
            .from({ user: collections.users })
            .where(({ user }) => eq(user.accountId, accountId)),
    [collections.users, accountId],
  );
  return result.data ?? [];
}

/**
 * Convenience: users on the team that owns the given ticket.
 */
export function useUsersForSupportTicket(
  supportTicketId: SupportTicketId,
): readonly AssignableUser[] {
  const { accountId } = useSupportTicketAccountId(supportTicketId);
  return useUsersForAccount(accountId);
}
